import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../api/config";
import JSLayout from "./js_layout";
import './css/job_seeker_dashboard.css';
import './css/employer_search.css';

export default function EmployerDetails() {
  const { id } = useParams(); // Extracts the route ID parameter
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanyAndJobs = async () => {
      setLoading(true);
      const token = localStorage.getItem("token");

      try {
        // Parallel lookup matches your specific C# API routing endpoints mapping
        const [profileRes, jobsRes] = await Promise.all([
          fetch(`${API_ENDPOINTS.EMPLOYERS}/${id}`, {
            headers: { "Authorization": `Bearer ${token}` }
          }),
          fetch(`${API_ENDPOINTS.EMPLOYERS}/${id}/jobs`, {
            headers: { "Authorization": `Bearer ${token}` }
          })
        ]);

        if (profileRes.ok) {
          const profileData = await profileRes.json();
          setCompany(profileData);
        }

        if (jobsRes.ok) {
          const jobsData = await jobsRes.json();
          setJobs(jobsData || []);
        }
      } catch (err) {
        console.error("Error fetching company details setup:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyAndJobs();
  }, [id]);

  // Helper utility function to construct a readable salary range string from data primitives
  const formatSalaryRange = (min, max, currency, per) => {
    if (!min && !max) return "Salary Undisclosed";
    
    const tokenCurrency = currency || "KES";
    const timingPeriod = per ? ` / ${per.toLowerCase()}` : "";
    
    if (min && max) {
      return `${tokenCurrency} ${min.toLocaleString()} - ${max.toLocaleString()}${timingPeriod}`;
    }
    return `${tokenCurrency} ${(min || max).toLocaleString()}${timingPeriod}`;
  };

  if (loading) return <JSLayout title="Loading..."><div>Analyzing employer records...</div></JSLayout>;
  if (!company) return <JSLayout title="Not Found"><div>Employer data profile couldn't be located.</div></JSLayout>;

  return (
    <JSLayout title="Company" titleEm="Profile ✦">
      <div className="jsd-fade">
        
        {/* Back navigation action utility */}
        <button 
          onClick={() => navigate(-1)} 
          style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', marginBottom: '1.5rem', padding: 0, fontSize: '0.9rem' }}
        >
          ← Back to discovery board
        </button>

        {/* 1. Brand Hero Panel (Updated with your exact C# Property schemas) */}
        <div className="jsd-profile-banner" style={{ background: '#fafafa', border: '1px solid #eee', color: 'var(--color-text)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="jsd-banner-text">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '4px' }}>
              <span style={{ fontSize: '2rem' }}>🏢</span>
              <div className="jsd-banner-title" style={{ color: 'var(--color-text)' }}>{company.businessName}</div>
            </div>
            <div className="jsd-banner-sub" style={{ color: '#555', fontWeight: '500' }}>
              📍 {company.location || 'Not Specified'} <span style={{ color: '#ccc', margin: '0 6px' }}>|</span> Sector: {company.businessType}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--color-text)' }}>{jobs.length}</div>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#666', fontWeight: 'bold', letterSpacing: '0.5px' }}>Open Vacancies</div>
          </div>
        </div>

        {/* 2. Primary Layout Separation Grid */}
        <div className="jsd-grid3" style={{ marginTop: '2rem' }}>
          
          {/* Main Content Column: Current Job Openings */}
          <div className="jsd-main-col">
            <div className="jsd-section-hdr">
              <div className="jsd-section-title">Current <em>openings</em></div>
            </div>

            <div className="jsd-jobs-list" style={{ marginTop: '1rem' }}>
              {jobs.length > 0 ? (
                jobs.map(job => (
                  <div key={job.id} className="jsd-job-card" onClick={() => navigate(`/jobs/${job.id}`)}>
                    <div className="jsd-job-info">
                      <div className="jsd-job-title">{job.title}</div>
                      <div className="jsd-job-company">{company.businessName}</div>
                      <div className="jsd-job-meta">
                        <span>💼 {job.jobType || 'Full-time'}</span>
                        <span className="jsd-dot">•</span>
                        <span>{formatSalaryRange(job.salaryMin, job.salaryMax, job.salaryCurrency, job.salaryPer)}</span>
                      </div>
                    </div>
                    <div className="jsd-job-right">
                      <div className="jsd-view-link">View Position →</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="emd-empty-container" style={{ background: '#fff' }}>
                  <div className="emd-empty-content">
                    <div className="emd-empty-icon">📂</div>
                    <p className="emd-empty-title">No active openings</p>
                    <p className="emd-empty-text">This company doesn't have open positions listed right now. Check back later!</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Column: Company Detailed Information */}
          <div className="jsd-side-col">
            <div className="jsd-panel-card">
              <div className="jsd-panel-title">About <em>employer</em></div>
              <p style={{ fontSize: '0.9rem', color: '#444', lineHeight: '1.5', marginTop: '0.5rem', whiteSpace: 'pre-line' }}>
                {company.bio || "No description overview detailed by this employer profile yet."}
              </p>
              
              <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '1.25rem 0' }} />
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#888', fontWeight: 'bold', letterSpacing: '0.3px' }}>Website</div>
                  {company.website ? (
                    <a href={company.website} target="_blank" rel="noreferrer" style={{ fontSize: '0.9rem', color: 'var(--color-text)', textDecoration: 'underline' }}>
                      {company.website}
                    </a>
                  ) : (
                    <span style={{ fontSize: '0.9rem', color: '#777' }}>Not provided</span>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#888', fontWeight: 'bold', letterSpacing: '0.3px' }}>Operating Scale</div>
                  <div style={{ fontSize: '0.9rem', color: '#333', fontWeight: '500', marginTop: '2px' }}>
                    {company.numberOfSeats ? `${company.numberOfSeats} Available Seats` : "Scale unlisted"}
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>

      </div>
    </JSLayout>
  );
}